/**
 * CommunicationBadgeContext
 *
 * Provides unread badge counts for the three communication modules:
 *   - Mail       → unread inbox threads
 *   - Chat       → unread direct messages (from others)
 *   - Channels   → unread channel activity (from others after last read)
 *
 * Design constraints (V1):
 *   - Does NOT touch Tasks or Approvals.
 *   - Does NOT redesign NotificationCenter.
 *   - Each module count is fetched independently; one 400/500 never blocks the others.
 *   - Lightweight: single polling endpoint + WebSocket event listeners for instant updates.
 *
 * Consumers:
 *   useCommunicationBadges()  → { mail, chat, channels, loading }
 *   invalidateChatBadge()     → call after reading a chat thread
 *   invalidateChannelBadge()  → call after reading a channel
 *   invalidateMailBadge()     → call after opening mail
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import api from "../utils/api";
import { authService } from "../utils/auth";
import { subscribeAuthSync, AUTH_SYNC_EVENTS } from "../utils/authSyncChannel";
import { COMM_BADGE_CHANNEL, createEventDeduper } from "../utils/commBadgeEvents";

// ─── Context ────────────────────────────────────────────────────────────────

const CommunicationBadgeContext = createContext({
  mail: 0,
  chat: 0,
  channels: 0,
  loading: true,
  invalidateChatBadge: () => {},
  invalidateChannelBadge: () => {},
  invalidateMailBadge: () => {},
  refresh: () => {},
});

// ─── Constants ───────────────────────────────────────────────────────────────

/** How often to poll the badge endpoint (ms). WebSocket events refine this. */
const POLL_INTERVAL_MS = 45_000;

/** Endpoint that returns { mail, chat, channels } */
const BADGE_ENDPOINT = "/api/workspace/communication-badges";

// ─── Provider ────────────────────────────────────────────────────────────────

export function CommunicationBadgeProvider({ children }) {
  const [badges, setBadges] = useState({ mail: 0, chat: 0, channels: 0 });
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);
  const mountedRef = useRef(true);

  /** Fetch all badge counts from the single endpoint. */
  const fetchBadges = useCallback(async () => {
    if (!authService.isAuthenticated()) {
      if (mountedRef.current) {
        setBadges({ mail: 0, chat: 0, channels: 0 });
        setLoading(false);
      }
      return;
    }
    try {
      const { data } = await api.get(BADGE_ENDPOINT);
      if (!mountedRef.current) return;
      setBadges({
        mail: data.mail ?? 0,
        chat: data.chat ?? 0,
        channels: data.channels ?? 0,
      });
    } catch {
      // Silently fail — stale counts are better than crashing the UI.
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  // ── Initial fetch + polling ──────────────────────────────────────────────
  useEffect(() => {
    mountedRef.current = true;
    if (authService.isAuthenticated()) {
      fetchBadges();
    } else {
      setBadges({ mail: 0, chat: 0, channels: 0 });
      setLoading(false);
    }

    pollRef.current = setInterval(() => {
      if (authService.isAuthenticated()) {
        fetchBadges();
      }
    }, POLL_INTERVAL_MS);

    const unsubSync = subscribeAuthSync((event) => {
      if (!mountedRef.current) return;
      if (event.type === AUTH_SYNC_EVENTS.AUTH_LOGOUT) {
        setBadges({ mail: 0, chat: 0, channels: 0 });
      } else if (
        event.type === AUTH_SYNC_EVENTS.AUTH_LOGIN ||
        event.type === AUTH_SYNC_EVENTS.AUTH_TOKEN_UPDATED
      ) {
        fetchBadges();
      }
    });

    return () => {
      mountedRef.current = false;
      clearInterval(pollRef.current);
      unsubSync();
    };
  }, [fetchBadges]);

  // ── WebSocket: listen for real-time chat events on the workspace WS ──────
  // We piggyback on the existing workspace WebSocket already maintained by
  // WorkspaceChats.  We use a BroadcastChannel so WorkspaceChats can post a
  // lightweight message without coupling to this provider directly.
  useEffect(() => {
    let bc;
    // One logical message must change the count once, however many tabs relay it.
    const deduper = createEventDeduper();
    try {
      bc = new BroadcastChannel(COMM_BADGE_CHANNEL);
      bc.onmessage = ({ data: evt }) => {
        if (!mountedRef.current) return;
        if (
          (evt?.type === "new_chat_message" || evt?.type === "new_channel_message") &&
          !deduper.accept(evt.eventId ? `${evt.type}:${evt.eventId}` : null)
        ) {
          return;
        }
        switch (evt?.type) {
          case "new_chat_message":
            setBadges((prev) => ({ ...prev, chat: (prev.chat || 0) + 1 }));
            break;
          case "chat_read":
            // Optimistic clear; full sync on next poll
            setBadges((prev) => ({ ...prev, chat: Math.max(0, prev.chat - (evt.delta || 1)) }));
            break;
          case "new_channel_message":
            setBadges((prev) => ({ ...prev, channels: (prev.channels || 0) + 1 }));
            break;
          case "channel_read":
            setBadges((prev) => ({ ...prev, channels: Math.max(0, prev.channels - (evt.delta || 1)) }));
            break;
          case "mail_read":
            setBadges((prev) => ({ ...prev, mail: Math.max(0, prev.mail - (evt.delta || 1)) }));
            break;
          case "refresh":
            fetchBadges();
            break;
          default:
            break;
        }
      };
    } catch {
      // BroadcastChannel not supported — polling alone will keep counts fresh.
    }
    return () => { try { bc?.close(); } catch {} };
  }, [fetchBadges]);

  // ── Imperative invalidation helpers (called from child components) ────────

  /** Trigger a full refresh (e.g. after reading a thread). */
  const refresh = useCallback(() => {
    fetchBadges();
  }, [fetchBadges]);

  /** Signal that a chat conversation was just opened → zero chat badge. */
  const invalidateChatBadge = useCallback(() => {
    setBadges((prev) => ({ ...prev, chat: 0 }));
    setTimeout(fetchBadges, 1500);
  }, [fetchBadges]);

  /** Signal that a channel was just opened → re-fetch for accuracy. */
  const invalidateChannelBadge = useCallback(() => {
    setTimeout(fetchBadges, 1500);
  }, [fetchBadges]);

  /** Signal that mail was just opened → re-fetch for accuracy. */
  const invalidateMailBadge = useCallback(() => {
    setTimeout(fetchBadges, 1500);
  }, [fetchBadges]);

  return (
    <CommunicationBadgeContext.Provider
      value={{
        ...badges,
        loading,
        refresh,
        invalidateChatBadge,
        invalidateChannelBadge,
        invalidateMailBadge,
      }}
    >
      {children}
    </CommunicationBadgeContext.Provider>
  );
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useCommunicationBadges() {
  return useContext(CommunicationBadgeContext);
}

export default CommunicationBadgeContext;
